import { useState, useMemo, useEffect } from "react"
import {
  Search,
  Filter,
  FileText,
  Video,
  File,
  Download,
  Eye,
  CheckCircle,
  Clock,
  AlertTriangle,
  Shield,
  Calendar,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react"

interface ComplianceMaterial {
  id: string
  title: string
  description: string
  fileUrl: string
  fileType: "pdf" | "video" | "document"
  category: "hipaa" | "privacy" | "security" | "regulatory" | "general"
  priority: "low" | "medium" | "high" | "critical"
  dueDate?: string
  completedAt?: string
  isRequired: boolean
  createdAt: string
}

interface ComplianceProgress {
  materialId: string
  completed: boolean
  completedAt?: string
  progress: number
}

export default function AgentComplianceMaterials() {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string>("all")
  const [selectedFileType, setSelectedFileType] = useState<string>("all")
  const [selectedPriority, setSelectedPriority] = useState<string>("all")
  const [showCompleted, setShowCompleted] = useState(true)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedMaterial, setSelectedMaterial] = useState<ComplianceMaterial | null>(null)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Mock data for compliance materials - expanded for pagination
  const [complianceMaterials] = useState<ComplianceMaterial[]>([
    {
      id: "1",
      title: "HIPAA Privacy Rule Training",
      description: "Comprehensive training on HIPAA privacy regulations and patient data protection requirements.",
      fileUrl: "/materials/hipaa-privacy.pdf",
      fileType: "pdf",
      category: "hipaa",
      priority: "critical",
      dueDate: "2024-02-15",
      isRequired: true,
      createdAt: "2024-01-01T00:00:00Z",
    },
    {
      id: "2",
      title: "Data Security Best Practices",
      description:
        "Video training covering data security protocols and best practices for handling sensitive information.",
      fileUrl: "/materials/data-security.mp4",
      fileType: "video",
      category: "security",
      priority: "high",
      dueDate: "2024-02-20",
      isRequired: true,
      createdAt: "2024-01-02T00:00:00Z",
    },
    {
      id: "3",
      title: "Medicare Compliance Guidelines",
      description: "Updated guidelines for Medicare compliance and regulatory requirements.",
      fileUrl: "/materials/medicare-compliance.pdf",
      fileType: "pdf",
      category: "regulatory",
      priority: "high",
      dueDate: "2024-02-25",
      isRequired: true,
      createdAt: "2024-01-03T00:00:00Z",
    },
    {
      id: "4",
      title: "Privacy Policy Updates",
      description: "Recent updates to company privacy policies and procedures.",
      fileUrl: "/materials/privacy-updates.docx",
      fileType: "document",
      category: "privacy",
      priority: "medium",
      dueDate: "2024-03-01",
      isRequired: false,
      createdAt: "2024-01-04T00:00:00Z",
    },
    {
      id: "5",
      title: "Cybersecurity Awareness Training",
      description: "Interactive training module on cybersecurity threats and prevention strategies.",
      fileUrl: "/materials/cybersecurity.mp4",
      fileType: "video",
      category: "security",
      priority: "high",
      isRequired: true,
      createdAt: "2024-01-05T00:00:00Z",
    },
    {
      id: "6",
      title: "General Compliance Handbook",
      description: "Comprehensive handbook covering all general compliance requirements and procedures.",
      fileUrl: "/materials/compliance-handbook.pdf",
      fileType: "pdf",
      category: "general",
      priority: "medium",
      isRequired: false,
      createdAt: "2024-01-06T00:00:00Z",
    },
    {
      id: "7",
      title: "HIPAA Security Rule Training",
      description: "Advanced training on HIPAA security requirements and technical safeguards.",
      fileUrl: "/materials/hipaa-security.pdf",
      fileType: "pdf",
      category: "hipaa",
      priority: "critical",
      dueDate: "2024-02-28",
      isRequired: true,
      createdAt: "2024-01-07T00:00:00Z",
    },
    {
      id: "8",
      title: "Anti-Fraud Training Module",
      description: "Training on identifying and preventing healthcare fraud and abuse.",
      fileUrl: "/materials/anti-fraud.mp4",
      fileType: "video",
      category: "regulatory",
      priority: "high",
      dueDate: "2024-03-05",
      isRequired: true,
      createdAt: "2024-01-08T00:00:00Z",
    },
    {
      id: "9",
      title: "Data Breach Response Procedures",
      description: "Step-by-step procedures for responding to data breaches and security incidents.",
      fileUrl: "/materials/breach-response.pdf",
      fileType: "pdf",
      category: "security",
      priority: "critical",
      dueDate: "2024-02-18",
      isRequired: true,
      createdAt: "2024-01-09T00:00:00Z",
    },
    {
      id: "10",
      title: "Employee Privacy Rights",
      description: "Understanding employee privacy rights and workplace monitoring policies.",
      fileUrl: "/materials/employee-privacy.docx",
      fileType: "document",
      category: "privacy",
      priority: "medium",
      isRequired: false,
      createdAt: "2024-01-10T00:00:00Z",
    },
    {
      id: "11",
      title: "Regulatory Compliance Updates 2024",
      description: "Latest regulatory changes and compliance requirements for the current year.",
      fileUrl: "/materials/regulatory-updates-2024.pdf",
      fileType: "pdf",
      category: "regulatory",
      priority: "high",
      dueDate: "2024-03-10",
      isRequired: true,
      createdAt: "2024-01-11T00:00:00Z",
    },
    {
      id: "12",
      title: "Information Security Policies",
      description: "Comprehensive overview of company information security policies and procedures.",
      fileUrl: "/materials/info-security-policies.pdf",
      fileType: "pdf",
      category: "security",
      priority: "medium",
      isRequired: false,
      createdAt: "2024-01-12T00:00:00Z",
    },
  ])

  // Mock progress data - expanded
  const [progress, setProgress] = useState<ComplianceProgress[]>([
    { materialId: "1", completed: true, completedAt: "2024-01-15T10:30:00Z", progress: 100 },
    { materialId: "2", completed: false, progress: 65 },
    { materialId: "3", completed: true, completedAt: "2024-01-20T14:15:00Z", progress: 100 },
    { materialId: "4", completed: false, progress: 0 },
    { materialId: "5", completed: false, progress: 30 },
    { materialId: "6", completed: false, progress: 0 },
    { materialId: "7", completed: false, progress: 45 },
    { materialId: "8", completed: true, completedAt: "2024-01-22T09:45:00Z", progress: 100 },
    { materialId: "9", completed: false, progress: 80 },
    { materialId: "10", completed: false, progress: 0 },
    { materialId: "11", completed: false, progress: 25 },
    { materialId: "12", completed: false, progress: 0 },
  ])

  const categories = [
    { value: "all", label: "All Categories" },
    { value: "hipaa", label: "HIPAA" },
    { value: "privacy", label: "Privacy" },
    { value: "security", label: "Security" },
    { value: "regulatory", label: "Regulatory" },
    { value: "general", label: "General" },
  ]

  const fileTypes = [
    { value: "all", label: "All Types" },
    { value: "pdf", label: "PDF" },
    { value: "video", label: "Video" },
    { value: "document", label: "Document" },
  ]

  const priorities = [
    { value: "all", label: "All Priorities" },
    { value: "critical", label: "Critical" },
    { value: "high", label: "High" },
    { value: "medium", label: "Medium" },
    { value: "low", label: "Low" },
  ]

  const filteredMaterials = useMemo(() => {
    return complianceMaterials.filter((material) => {
      const materialProgress = progress.find((p) => p.materialId === material.id)
      const isCompleted = materialProgress?.completed || false

      const matchesSearch =
        material.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        material.description.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesCategory = selectedCategory === "all" || material.category === selectedCategory
      const matchesFileType = selectedFileType === "all" || material.fileType === selectedFileType
      const matchesPriority = selectedPriority === "all" || material.priority === selectedPriority
      const matchesCompletion = showCompleted || !isCompleted

      return matchesSearch && matchesCategory && matchesFileType && matchesPriority && matchesCompletion
    })
  }, [complianceMaterials, progress, searchTerm, selectedCategory, selectedFileType, selectedPriority, showCompleted])

  // Pagination calculations
  const totalItems = filteredMaterials.length
  const totalPages = Math.ceil(totalItems / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedMaterials = filteredMaterials.slice(startIndex, endIndex)

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, selectedCategory, selectedFileType, selectedPriority, showCompleted])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case "pdf":
        return <FileText className="w-5 h-5 text-red-600" />
      case "video":
        return <Video className="w-5 h-5 text-blue-600" />
      case "document":
        return <File className="w-5 h-5 text-green-600" />
      default:
        return <File className="w-5 h-5 text-gray-600" />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical":
        return "bg-red-100 text-red-800 border-red-200"
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-200"
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-200"
      case "low":
        return "bg-green-100 text-green-800 border-green-200"
      default:
        return "bg-gray-100 text-gray-800 border-gray-200"
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "hipaa":
        return "bg-purple-100 text-purple-800"
      case "privacy":
        return "bg-blue-100 text-blue-800"
      case "security":
        return "bg-red-100 text-red-800"
      case "regulatory":
        return "bg-orange-100 text-orange-800"
      case "general":
        return "bg-gray-100 text-gray-800"
      default:
        return "bg-gray-100 text-gray-800"
    }
  }

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false
    return new Date(dueDate) < new Date()
  }

  const markAsCompleted = (materialId: string) => {
    setProgress((prev) =>
      prev.map((p) =>
        p.materialId === materialId
          ? { ...p, completed: true, completedAt: new Date().toISOString(), progress: 100 }
          : p,
      ),
    )
  }

  const totalMaterials = complianceMaterials.length
  const completedMaterials = progress.filter((p) => p.completed).length
  const requiredMaterials = complianceMaterials.filter((m) => m.isRequired).length
  const completedRequired = complianceMaterials.filter(
    (m) => m.isRequired && progress.find((p) => p.materialId === m.id)?.completed,
  ).length
  const overdueMaterials = complianceMaterials.filter(
    (m) => isOverdue(m.dueDate) && !progress.find((p) => p.materialId === m.id)?.completed,
  ).length

  const exportToCSV = () => {
    const csvData = filteredMaterials.map((material) => {
      const materialProgress = progress.find((p) => p.materialId === material.id)
      return {
        Title: material.title,
        Category: material.category,
        Priority: material.priority,
        "File Type": material.fileType,
        Required: material.isRequired ? "Yes" : "No",
        "Due Date": material.dueDate || "N/A",
        Completed: materialProgress?.completed ? "Yes" : "No",
        "Completed Date": materialProgress?.completedAt || "N/A",
        Progress: `${materialProgress?.progress || 0}%`,
      }
    })

    const headers = Object.keys(csvData[0] || {})
    const csvContent = [
      headers.join(","),
      ...csvData.map((row) => headers.map((header) => `"${row[header as keyof typeof row]}"`).join(",")),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "compliance-materials.csv"
    a.click()
    window.URL.revokeObjectURL(url)
  }

  // Pagination Controls Component
  const PaginationControls = () => {
    const getVisiblePages = () => {
      const delta = 2
      const range = []
      const rangeWithDots = []

      for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
        range.push(i)
      }

      if (currentPage - delta > 2) {
        rangeWithDots.push(1, "...")
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (currentPage + delta < totalPages - 1) {
        rangeWithDots.push("...", totalPages)
      } else if (totalPages > 1) {
        rangeWithDots.push(totalPages)
      }

      return rangeWithDots
    }

    if (totalPages <= 1) return null

    return (
      <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div className="flex items-center space-x-2">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
              <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{" "}
              <span className="font-medium">{totalItems}</span> results
            </p>
            <select
              value={itemsPerPage}
              onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
              className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First page"
              >
                <span className="sr-only">First page</span>
                <span className="text-sm">⟪</span>
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous page"
              >
                <span className="sr-only">Previous</span>
                <span className="text-sm">‹</span>
              </button>

              {getVisiblePages().map((page, index) => (
                <button
                  key={index}
                  onClick={() => typeof page === "number" && handlePageChange(page)}
                  disabled={page === "..."}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                    page === currentPage
                      ? "z-10 bg-blue-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                      : page === "..."
                        ? "text-gray-700 ring-1 ring-inset ring-gray-300 cursor-default"
                        : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0"
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next page"
              >
                <span className="sr-only">Next</span>
                <span className="text-sm">›</span>
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last page"
              >
                <span className="sr-only">Last page</span>
                <span className="text-sm">⟫</span>
              </button>
            </nav>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Compliance Materials</h2>
          <p className="mt-1 text-sm text-gray-600">Stay compliant with required training and documentation</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            onClick={exportToCSV}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Materials</dt>
                  <dd className="text-lg font-medium text-gray-900">{totalMaterials}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                  <dd className="text-lg font-medium text-gray-900">{completedMaterials}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-orange-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Required Progress</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {completedRequired}/{requiredMaterials}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Overdue</dt>
                  <dd className="text-lg font-medium text-gray-900">{overdueMaterials}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg">
        <div className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search materials..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {showFilters ? <ChevronUp className="w-4 h-4 ml-2" /> : <ChevronDown className="w-4 h-4 ml-2" />}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {categories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">File Type</label>
                  <select
                    value={selectedFileType}
                    onChange={(e) => setSelectedFileType(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {fileTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select
                    value={selectedPriority}
                    onChange={(e) => setSelectedPriority(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {priorities.map((priority) => (
                      <option key={priority.value} value={priority.value}>
                        {priority.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showCompleted"
                    checked={showCompleted}
                    onChange={(e) => setShowCompleted(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="showCompleted" className="ml-2 block text-sm text-gray-700">
                    Show completed
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Compliance Materials Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Material
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedMaterials.map((material) => {
                const materialProgress = progress.find((p) => p.materialId === material.id)
                const isCompleted = materialProgress?.completed || false
                const progressPercent = materialProgress?.progress || 0
                const overdue = isOverdue(material.dueDate)

                return (
                  <tr key={material.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 mr-3">{getFileIcon(material.fileType)}</div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{material.title}</div>
                          <div className="text-sm text-gray-500 max-w-xs truncate">{material.description}</div>
                          {material.isRequired && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-1">
                              Required
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(material.category)}`}
                      >
                        {material.category.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(material.priority)}`}
                      >
                        {material.priority.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${isCompleted ? "bg-green-500" : "bg-blue-500"}`}
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{progressPercent}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {material.dueDate ? (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                          <span
                            className={`text-sm ${overdue && !isCompleted ? "text-red-600 font-medium" : "text-gray-600"}`}
                          >
                            {new Date(material.dueDate).toLocaleDateString()}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {isCompleted ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Completed
                        </span>
                      ) : overdue ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <AlertTriangle className="w-3 h-3 mr-1" />
                          Overdue
                        </span>
                      ) : progressPercent > 0 ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Clock className="w-3 h-3 mr-1" />
                          In Progress
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Not Started
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => setSelectedMaterial(material)}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                        >
                          View
                        </button>
                        {!isCompleted && (
                          <button
                            onClick={() => markAsCompleted(material.id)}
                            className="text-green-600 hover:text-green-900 text-sm font-medium"
                          >
                            Complete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <PaginationControls />
      </div>

      {filteredMaterials.length === 0 && (
        <div className="text-center py-12">
          <Shield className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No compliance materials found</h3>
          <p className="mt-1 text-sm text-gray-500">Try adjusting your search criteria or filters.</p>
        </div>
      )}

      {/* Material Details Modal */}
      {selectedMaterial && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{selectedMaterial.title}</h3>
                <div className="flex items-center space-x-2 mt-2">
                  {getFileIcon(selectedMaterial.fileType)}
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(selectedMaterial.priority)}`}
                  >
                    {selectedMaterial.priority.toUpperCase()}
                  </span>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(selectedMaterial.category)}`}
                  >
                    {selectedMaterial.category.toUpperCase()}
                  </span>
                  {selectedMaterial.isRequired && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Required
                    </span>
                  )}
                </div>
              </div>
              <button onClick={() => setSelectedMaterial(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Description</h4>
                <p className="text-sm text-gray-600">{selectedMaterial.description}</p>
              </div>

              {selectedMaterial.dueDate && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Due Date</h4>
                  <p className={`text-sm ${isOverdue(selectedMaterial.dueDate) ? "text-red-600" : "text-gray-600"}`}>
                    {new Date(selectedMaterial.dueDate).toLocaleDateString()}
                    {isOverdue(selectedMaterial.dueDate) && " (Overdue)"}
                  </p>
                </div>
              )}

              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Progress</h4>
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div
                    className={`h-2 rounded-full ${
                      progress.find((p) => p.materialId === selectedMaterial.id)?.completed
                        ? "bg-green-500"
                        : "bg-blue-500"
                    }`}
                    style={{ width: `${progress.find((p) => p.materialId === selectedMaterial.id)?.progress || 0}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600">
                  {progress.find((p) => p.materialId === selectedMaterial.id)?.progress || 0}% complete
                </p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => window.open(selectedMaterial.fileUrl, "_blank")}
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Open Material
                </button>
                {!progress.find((p) => p.materialId === selectedMaterial.id)?.completed && (
                  <button
                    onClick={() => {
                      markAsCompleted(selectedMaterial.id)
                      setSelectedMaterial(null)
                    }}
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark Complete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
